import type { ParsedUrlQueryInput } from 'querystring';

import type { NextRouter } from 'next/router';

import type { AppData, StepCategory } from '../../types/app_data';

/**
 * Direction of navigation.
 * Used primarily for navigation animation.
 */
export enum Direction {
  Left,
  Right,
}

/**
 * Use in stepManager when getting next step needs special logic.
 * enable: indicates wether special logic is enabled.
 * nextStepId: indicates the id of the next step should be go when special logic enabled.
 */
export interface SpecialNextStepType {
  enable: (appData: AppData) => boolean;
  nextStepId: string;
}

/**
 * Represents the 'specification' of a step in the funnel.
 */
export interface Step {
  // A unique identifier for the step.
  id: string;
  // Pathname for the step.
  pathname: string;
  /**
   * Exclude this step from the progress calculation, so that this step won't contribute to the progress percentage.
   */
  excludeFromProgressCalculation?: boolean | ((appData: AppData) => boolean);
  // Function to determine if this step can be shown.
  // NOTE: If a step cannot be shown, the system will try to find
  // subsequent steps that can be shown.
  canShow: (appData: AppData) => boolean;
  /**
   * Specify whether to skip id check when evaluating `canShow` callback.
   *
   * By default, when evaluating `getCanShowSteps`, steps with the same id as current step will always
   * be pushed into the `showSteps` array, even if the `canShow` result is false.
   *
   * Setting this flag will skip the id check, so that only steps with `canShow` result as true will be
   * pushed into the `showSteps` array.
   */
  canShowSkipIdCheck?: boolean;
  // Function to determine if this step has all the data
  // it needs to be able to render.
  // NOTE: This is different from `canShow`. If `hasData` returns `false`,
  // the system will simply stop from moving further.
  hasData: (appData: AppData) => boolean;
  // Function to determine if the `appData` contains the data that this
  // step is supposed to collect.
  // NOTE: This is primarily used to show the 'go forward' arrow button on the
  // step header.
  hasOwnData: (appData: AppData, router?: NextRouter) => boolean;
  // Specify whether a step in front of this can press the back button and land on this step.
  // By default this is `true`. However, for some steps we may not want this to happen. For
  // example, if we have a 'loader' in between two steps: Step A -> Step Loader -> Step B, then
  // we may want the back button on `Step B` to take us to `Step A` instead of `Step Loader`.
  // To accomplish this, set `allowBackToSelf` field for `Step Loader` to `false`.
  allowBackToSelf?: boolean;
  /**
   * Normally the step goes one way in order, when some of the steps needs to go over again,
   * for example, user setup maintenance reminder for one car, and wants to setup for another car,
   * we should go over the maintenance reminder setup steps again.
   * It goes like: `show car list` -> select a car to setup -> `maintenance reminder steps`
   * -> `show car list` -> select another car to setup -> `maintenance reminder steps` -> ...
   * When the user want to quit the loop, turn off the value of specialNextStep.enable.
   *
   * TODO: To improve flexibility, we can change this to a function `(appData: AppData) => string | undefined`.
   */
  specialNextStep?: SpecialNextStepType;
  /**
   * When coming to this step, set the query params as the returned data of this field.
   */
  queryParams?: (appData?: AppData, router?: NextRouter) => ParsedUrlQueryInput;
  /**
   * Returns the category of step, e.g. addCarYear, addCarModel, addCarSubModel all belongs to CARS category.
   */
  category?: (appData?: AppData) => StepCategory;
}
